import {Box, HBox} from '@buncha/components/core/view';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {Animated} from 'react-native';
import BorderRightTickIcon from '@buncha/components/core/icon/BorderRightTickIcon';
import CrossIcon from '@buncha/components/core/icon/CrossIcon';
import {CustomImage} from '@buncha/components/core/Image';
import {DeliveryLocationStatus} from '@buncha/batchDetails/types';
import If from '@buncha/components/conditional/If';
import {scaleFonts} from '@buncha/appStyles/style/font';
import {styles} from './styles';
import {useColorThemeType} from '@buncha/appStyles/hooks/theme';

interface Props {
  index: number;
  step: {
    isCompleted: boolean;
    zIndex: number;
    content: React.ReactNode;
    isNextCompleted: boolean;
  };
  length: number;
  fillTop: Animated.Value;
  fillBottom: Animated.Value;
  deliveryLocationStatus?: DeliveryLocationStatus;
}

const VerticalOrderStepper = ({
  index,
  step,
  length,
  fillTop,
  fillBottom,
  deliveryLocationStatus,
}: Props) => {
  const theme = useColorThemeType();
  const isFirst = index === 0;
  const isLast = index === length - 1;
  const [contentHeight, setContentHeight] = useState(0);
  const [isAnimated, setIsAnimated] = useState(false);
  const halfHeight = scaleFonts(Math.ceil(contentHeight / 2), 'height');
  const isDeliveryFailed =
    deliveryLocationStatus === DeliveryLocationStatus.deliveryFailed;
  const isDelivered =
    deliveryLocationStatus === DeliveryLocationStatus.delivered;
  const onTheWay = deliveryLocationStatus === DeliveryLocationStatus.onTheWay;
  const isCancelled =
    deliveryLocationStatus === DeliveryLocationStatus.cancelled;

  const topFillHeight = fillTop.interpolate({
    inputRange: [0, 1],
    outputRange: [0, halfHeight],
  });

  const bottomFillHeight = fillBottom.interpolate({
    inputRange: [0, 1],
    outputRange: [0, halfHeight],
  });

  const fillProgress = useRef(new Animated.Value(0)).current;

  const getIcon = useMemo(() => {
    switch (index) {
      case 1:
        return (
          <CustomImage
            style={isCancelled ? styles.cancelImageStyle : styles.imageStyle}
            source={
              isCancelled
                ? require('@buncha/resource/images/bagRoundRed.png')
                : require('@buncha/resource/images/cart.png')
            }
          />
        );
      case 2:
        return (
          <CustomImage
            style={styles.imageStyle}
            source={require('@buncha/resource/images/van.png')}
          />
        );
      case 3:
        if (isDeliveryFailed)
          return (
            <Box
              style={[
                styles.iconStyle,
                {
                  backgroundColor: theme?.background.red[300],
                },
              ]}>
              <CrossIcon
                width="24"
                height="24"
                viewBox="2 2 20 20"
                stroke={theme?.background.white[100]}
              />
            </Box>
          );
        if (isDelivered)
          return (
            <BorderRightTickIcon
              width={34}
              height={34}
              viewBox="0 0 37 37"
              fill={theme?.background.blue[300]}
              style={[
                styles.icon,
                {
                  top: halfHeight - 17,
                },
              ]}
            />
          );
        return (
          <CustomImage
            style={styles.imageStyle}
            source={require('@buncha/resource/images/house.png')}
          />
        );
      default:
        if (isCancelled)
          return (
            <CustomImage
              style={styles.cancelImageStyle}
              source={require('@buncha/resource/images/bagRound.png')}
            />
          );
        return (
          <BorderRightTickIcon
            width={44}
            height={44}
            viewBox="0 0 37 37"
            fill={theme?.background.blue[300]}
            style={[
              styles.enableIcon,
              {
                top: halfHeight - 17,
              },
            ]}
          />
        );
    }
  }, [
    halfHeight,
    index,
    isCancelled,
    isDelivered,
    isDeliveryFailed,
    theme?.background.blue,
    theme?.background.red,
    theme?.background.white,
  ]);

  useEffect(() => {
    const shouldAnimate =
      (step.isCompleted || step.isNextCompleted) && !isAnimated;

    if (shouldAnimate)
      Animated.timing(fillProgress, {
        toValue: 1,
        duration: 600,
        delay: index * 300,
        useNativeDriver: false,
      }).start();
    setIsAnimated(true);
  }, [step.isCompleted, step.isNextCompleted, fillProgress, index, isAnimated]);

  return (
    <HBox key={index} style={[styles.row]}>
      <Box style={styles.iconColumn}>
        <Box
          style={[
            styles.halfBar,
            // eslint-disable-next-line react-native/no-inline-styles
            {
              height: scaleFonts(halfHeight),
              backgroundColor: theme?.background.gray[200],
              borderTopLeftRadius: isFirst ? 25 : 0,
              borderTopRightRadius: isFirst ? 25 : 0,
            },
          ]}>
          <Animated.View
            style={[
              styles.fillBar,
              {
                height: topFillHeight,
                backgroundColor:
                  isDeliveryFailed && index === 3
                    ? theme?.background.red[300]
                    : theme?.background.blue[300],
              },
            ]}
          />
        </Box>
        <If condition={!step.isNextCompleted}>{getIcon}</If>

        <If condition={step.isNextCompleted}>
          <BorderRightTickIcon
            width={34}
            height={34}
            viewBox="0 0 37 37"
            fill={theme?.background.blue[300]}
            style={[
              styles.icon,
              {
                top: halfHeight - 17,
              },
            ]}
          />
        </If>

        <Box
          style={[
            styles.halfBar,
            // eslint-disable-next-line react-native/no-inline-styles
            {
              height: scaleFonts(halfHeight),
              backgroundColor: isLast
                ? theme?.background.white[100]
                : theme?.background.gray[200],
              borderBottomLeftRadius: isLast ? 25 : 0,
              borderBottomRightRadius: isLast ? 25 : 0,
            },
          ]}>
          <If condition={!isLast}>
            <Animated.View
              style={[
                styles.fillBar,
                {
                  height: bottomFillHeight,
                  backgroundColor:
                    isDeliveryFailed && index === 2
                      ? theme?.background.red[300]
                      : theme?.background.blue[300],
                },
              ]}
            />
          </If>
        </Box>
      </Box>

      <Box
        onLayout={e => setContentHeight(e.nativeEvent.layout.height)}
        style={[styles.contentContainer]}>
        <Box
          style={[
            styles.contentBox,
            {
              borderColor: theme?.background.gray[200],
              marginBottom:
                onTheWay && index === length - 2 ? scaleFonts(-30) : undefined,
            },
          ]}>
          {step.content}
        </Box>
      </Box>
    </HBox>
  );
};

export default React.memo(VerticalOrderStepper);
